import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { Invoice } from "@common/types/invoice";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { filterTemplatesForDocument } from "@components/accounting/schedules/documentScheduleTemplates";
import { resolvePickerTemplates } from "@components/accounting/schedules/schedulePickerTemplates";
import { useInvoiceSchedulePicker } from "@components/invoices/useInvoiceSchedulePicker";
import {
    isScheduleCellInteractive,
    toScheduleCardValues,
} from "@components/invoices/views/scheduleCellState";
import { ScheduleCellContent } from "@components/invoices/views/ScheduleCellContent";
import { InvoicesViewCallbacks, InvoicesViewOptions } from "../types";

export const ScheduleCell = memo(
    ({ row, table }: CellContext<ViewTableFeatures, Invoice, unknown>) => {
        const meta = useViewTableMeta<
            InvoicesViewCallbacks,
            InvoicesViewOptions
        >(table);
        const invoice = row.original;
        const { schedulesExpenses: isExpensesEnabled } = useTypedFlags();

        const scheduleTemplates = useMemo(
            () =>
                filterTemplatesForDocument({
                    templates: resolvePickerTemplates({
                        templates: meta.options?.scheduleTemplates,
                        entityId: invoice.entityId,
                        prioritizedTemplateId:
                            meta.options?.prioritizedScheduleTemplateId,
                    }),
                    documentType: ScheduleSourceType.invoice,
                    isExpensesEnabled,
                }),
            [
                isExpensesEnabled,
                meta.options?.scheduleTemplates,
                meta.options?.prioritizedScheduleTemplateId,
                invoice.entityId,
            ],
        );

        const { state, pickerProps, overlays } = useInvoiceSchedulePicker({
            invoice,
            callbacks: meta.callbacks,
        });

        if (!isScheduleCellInteractive(state)) {
            return (
                <ViewTableCell>
                    <ScheduleCellContent state={state} />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <SchedulePicker
                    templates={scheduleTemplates}
                    applied={toScheduleCardValues(state)}
                    prioritizedTemplateId={
                        meta.options?.prioritizedScheduleTemplateId
                    }
                    {...pickerProps}
                />
                {overlays}
            </ViewTableCell>
        );
    },
);

ScheduleCell.displayName = "ScheduleCell";
